import { defineStore } from 'pinia'
import { ref } from 'vue'

import api from '@/utils/api'

export type NavigationRecord = {
  uniqueViewName: string
  ids: number[]
  nextUrl: string | null
  previousUrl: string | null
}

export const useRecordNavigationStore = defineStore('recordNavigation', () => {
  // State
  const hide = ref(true)
  const uniqueViewName = ref('')
  const ids = ref<number[]>([])
  const nextUrl = ref<string | null>(null)
  const previousUrl = ref<string | null>(null)
  const previousRecord = ref<Record<string, NavigationRecord>>({})
  const isRecordRkListe = ref<Record<number, boolean>>({})

  // Actions
  function setHidden(payload: boolean) {
    hide.value = payload
  }

  function setUniqueViewName(payload: string) {
    uniqueViewName.value = payload
  }

  function setIds(payload: number[]) {
    ids.value = payload
  }

  function setNextUrl(payload: string | null) {
    nextUrl.value = payload
  }

  function setPreviousUrl(payload: string | null) {
    previousUrl.value = payload
  }

  function setIsRecordRkListe(records: any[] | Record<number, boolean>) {
    // Wenn bereits ein Objekt übergeben wird (aus localStorage), direkt verwenden
    if (!Array.isArray(records)) {
      Object.keys(isRecordRkListe.value).forEach(key => { delete isRecordRkListe.value[Number(key)] })
      Object.assign(isRecordRkListe.value, records)
      return
    }
    // Wenn ein Array übergeben wird, in Map umwandeln
    const map = { ...isRecordRkListe.value }
    for (const rec of records) {
      map[rec.id] = rec?.rkliste || false
    }
    Object.keys(isRecordRkListe.value).forEach(key => { delete isRecordRkListe.value[Number(key)] })
    Object.assign(isRecordRkListe.value, map)
  }

  function saveCurrentRecord(payload: string) {
    previousRecord.value[payload] = {
      previousUrl: previousUrl.value,
      nextUrl: nextUrl.value,
      uniqueViewName: uniqueViewName.value,
      ids: ids.value,
    }
  }

  function loadPreviousRecord(payload: string) {
    if (payload && previousRecord.value[payload]) {
      const prev = previousRecord.value[payload]
      previousUrl.value = prev.previousUrl
      nextUrl.value = prev.nextUrl
      uniqueViewName.value = prev.uniqueViewName
      ids.value = prev.ids
    }
  }

  async function loadNextUrl() {
    if (nextUrl.value) {
      const data = await api.get(nextUrl.value, {}, true, true, false)
      if (data?.results) {
        const newIds: number[] = ids.value.slice(0)
        for (const record of data.results) {
          if (record.id) {
            newIds.push(record.id)
          }
        }
        ids.value = newIds
        setIsRecordRkListe(data.results)
      }
      if (data) {
        nextUrl.value = data.next
      }
    }
  }

  async function loadPreviousUrl() {
    if (previousUrl.value) {
      const data = await api.get(previousUrl.value, {}, true, true, false)
      if (data?.results) {
        const newIds: number[] = ids.value.slice(0)
        for (const record of data.results.reverse()) {
          if (record.id) {
            newIds.unshift(record.id)
          }
        }
        ids.value = newIds
        setIsRecordRkListe(data.results)
      }
      if (data) {
        previousUrl.value = data.previous
      }
    }
  }

  return {
    hide,
    uniqueViewName,
    ids,
    nextUrl,
    previousUrl,
    previousRecord,
    isRecordRkListe,
    setHidden,
    setUniqueViewName,
    setIds,
    setNextUrl,
    setPreviousUrl,
    setIsRecordRkListe,
    saveCurrentRecord,
    loadPreviousRecord,
    loadNextUrl,
    loadPreviousUrl
  }
})